import { computed, Injectable, signal } from '@angular/core';

import { ROLE_PERMISSIONS } from './permissions';
import type { OrgRole } from './orgs.types';

/** Every role, weakest first — the order the picker offers them in. */
export const VIEW_AS_ROLES: readonly OrgRole[] = ['viewer', 'member', 'admin', 'owner'];

/** Title case for a role, for the picker and the mode menu. */
export function roleLabel(role: OrgRole): string {
  return role.charAt(0).toUpperCase() + role.slice(1);
}

/**
 * "View as role" — a client-side downgrade of what the UI will show
 * (#253).
 *
 * Internal admins see every affordance in the product, which makes it hard
 * to answer the question this exists for: *is this feature actually hidden
 * from a viewer?* The alternative is keeping a dummy user per role and
 * signing in and out of them.
 *
 * ⚠️ **This is a lens, not a login.** Nothing about the session changes: the
 * ID token, the custom claims and every server-side check are untouched, so
 * the answer it gives is "what does the UI show this role", never "what would
 * the server allow". A feature hidden only in the client still looks hidden
 * here — that gap is what `firestore.rules` and the callables' own checks are
 * for, and this cannot test them.
 *
 * ⚠️ **It can only ever REMOVE.** `OrgService.can` intersects the borrowed
 * role with the caller's real one, so a lens never grants a permission the
 * account does not already hold. That is what makes the mode safe to expose
 * at all — and why it needs no server, no claim and no audit trail: anyone
 * could already hide their own buttons with devtools, and nobody can show
 * themselves a button that works.
 *
 * Deliberately **in memory only**. A lens that survives a reload is a lens
 * you forget you are wearing, and the next puzzling "why can I not approve
 * this?" is an hour of someone's day. The avatar wears it, and two clicks
 * end it (avatar, Stop).
 */
@Injectable({ providedIn: 'root' })
export class ViewAsService {
  private readonly _role = signal<OrgRole | null>(null);

  /** The borrowed role, or null when the user is themselves. */
  readonly role = this._role.asReadonly();

  /** True while a lens is on — what the avatar's mode ring renders from. */
  readonly active = computed(() => this._role() !== null);

  /**
   * Put a lens on, or take it off with `null`.
   *
   * Unauthorized on purpose: since the mode can only subtract, there is
   * nothing here to protect. The CONTROL is offered to internal admins only,
   * because they are the ones it helps.
   */
  set(role: OrgRole | null): void {
    this._role.set(role && ROLE_PERMISSIONS[role] ? role : null);
  }

  clear(): void {
    this._role.set(null);
  }
}
